/**
 * Mix bus structure, ducking and tinnitus.
 *
 *   voices ──► weapons ─┐
 *              world  ──┤─► duck ─► tinnitus LP ─► master ─► limiter ─► out
 *              ui     ──┤
 *              music  ──┤
 *              verb   ──┘
 *
 * Ducking: every gunshot briefly pushes world / music / ambience down, so the gun
 * always owns the moment. Tinnitus: a nearby explosion drops a steep low-pass on
 * every bus and rings a pair of high tones, both recovering over a few seconds —
 * the signature Call of Duty concussion effect.
 */
import { gain, bq, limiter, compressor } from './synth/nodes.js';

export const BUS_NAMES = ['weapons', 'world', 'ui', 'music', 'verb'];

const BUS_DEFAULTS = {
  weapons: { level: 1.00, duckable: 0.15, comp: { threshold: -14, ratio: 3, attack: 0.002, release: 0.16 } },
  world: { level: 0.85, duckable: 1.00, comp: { threshold: -18, ratio: 3.5, attack: 0.004, release: 0.20 } },
  ui: { level: 0.75, duckable: 0.35, comp: { threshold: -16, ratio: 2.5, attack: 0.003, release: 0.15 } },
  music: { level: 0.55, duckable: 1.00, comp: { threshold: -20, ratio: 4, attack: 0.010, release: 0.35 } },
  verb: { level: 0.80, duckable: 0.55, comp: { threshold: -20, ratio: 3, attack: 0.006, release: 0.25 } },
};

export class Buses {
  /** @param {BaseAudioContext} ac */
  constructor(ac, { masterLevel = 0.9 } = {}) {
    this.ac = ac;
    this.masterLevel = masterLevel;

    const lim = limiter(ac);
    this.limiter = lim;
    this.master = gain(ac, masterLevel);
    this.master.connect(lim.input);
    lim.output.connect(ac.destination);

    /** @type {Record<string, {input:GainNode, level:GainNode, duck:GainNode, lp:BiquadFilter, comp:DynamicsCompressorNode, duckable:number}>} */
    this.bus = {};
    for (const name of BUS_NAMES) {
      const cfg = BUS_DEFAULTS[name];
      const input = gain(ac, 1);
      const comp = compressor(ac, cfg.comp);
      const duck = gain(ac, 1);
      const lp = bq(ac, 'lowpass', 21000, 0.7);
      const level = gain(ac, cfg.level);
      input.connect(comp).connect(duck).connect(lp).connect(level).connect(this.master);
      this.bus[name] = { input, comp, duck, lp, level, duckable: cfg.duckable, baseLevel: cfg.level };
    }

    // tinnitus tone bus — bypasses the low-pass so it can still be heard
    this.tinnitusGain = gain(ac, 0);
    this.tinnitusGain.connect(this.master);
    this._tone = null;
    this.tinnitusUntil = 0;
    this._duckUntil = 0;
  }

  get(name) {
    return this.bus[name] || this.bus.world;
  }

  input(name) {
    return this.get(name).input;
  }

  /**
   * Duck everything duckable. `amount` 0..1 is how far down (1 = full duck).
   * @param {number} amount @param {number} hold seconds at the ducked level
   * @param {number} release recovery time constant
   */
  duck(amount = 0.55, hold = 0.02, release = 0.22) {
    const t = this.ac.currentTime;
    const a = Math.max(0, Math.min(1, amount));
    for (const name of BUS_NAMES) {
      const b = this.bus[name];
      if (b.duckable <= 0) continue;
      const target = 1 - a * b.duckable;
      const p = b.duck.gain;
      p.cancelScheduledValues(t);
      p.setValueAtTime(p.value, t);
      p.linearRampToValueAtTime(target, t + 0.008);
      p.setValueAtTime(target, t + 0.008 + hold);
      p.setTargetAtTime(1, t + 0.008 + hold, release);
    }
    this._duckUntil = t + hold + release * 4;
  }

  /**
   * Concussion. `power` 0..1. Steep low-pass on every bus plus a ringing pair of
   * tones, both recovering over `seconds`.
   */
  tinnitus(power = 1, seconds = 4.5) {
    const ac = this.ac;
    const t = ac.currentTime;
    const p = Math.max(0, Math.min(1, power));
    if (p < 0.02) return;
    const cutoff = 320 + 2400 * (1 - p);

    for (const name of BUS_NAMES) {
      const b = this.bus[name];
      const f = b.lp.frequency;
      f.cancelScheduledValues(t);
      f.setValueAtTime(Math.min(21000, Math.max(cutoff, f.value)), t);
      f.linearRampToValueAtTime(cutoff, t + 0.03);
      f.exponentialRampToValueAtTime(21000, t + 0.03 + seconds * (0.6 + 0.4 * p));
      const g = b.duck.gain;
      g.cancelScheduledValues(t);
      g.setValueAtTime(g.value, t);
      g.linearRampToValueAtTime(1 - 0.55 * p * b.duckable, t + 0.02);
      g.setTargetAtTime(1, t + 0.02, seconds * 0.35);
    }

    // the ring itself: two detuned high tones, slightly wobbling
    this._stopTone();
    const o1 = ac.createOscillator();
    const o2 = ac.createOscillator();
    const lfo = ac.createOscillator();
    const lfoG = gain(ac, 12);
    o1.type = 'sine'; o2.type = 'sine'; lfo.type = 'sine';
    o1.frequency.value = 4680;
    o2.frequency.value = 6120;
    lfo.frequency.value = 0.7;
    lfo.connect(lfoG);
    lfoG.connect(o1.frequency);
    const g1 = gain(ac, 1);
    const g2 = gain(ac, 0.45);
    o1.connect(g1).connect(this.tinnitusGain);
    o2.connect(g2).connect(this.tinnitusGain);
    const tg = this.tinnitusGain.gain;
    tg.cancelScheduledValues(t);
    tg.setValueAtTime(0, t);
    tg.linearRampToValueAtTime(0.09 * p, t + 0.02);
    tg.setTargetAtTime(0, t + 0.05, seconds * 0.3);
    o1.start(t); o2.start(t); lfo.start(t);
    const stop = t + seconds + 1.5;
    o1.stop(stop); o2.stop(stop); lfo.stop(stop);
    this._tone = { o1, o2, lfo, stop };
    this.tinnitusUntil = stop;
  }

  _stopTone() {
    if (!this._tone) return;
    const t = this.ac.currentTime;
    try { this._tone.o1.stop(t); this._tone.o2.stop(t); this._tone.lfo.stop(t); } catch { /* already stopped */ }
    this._tone = null;
  }

  setBusLevel(name, v) {
    const b = this.bus[name];
    if (!b) return;
    b.level.gain.setTargetAtTime(v, this.ac.currentTime, 0.05);
  }

  setMaster(v) {
    this.masterLevel = v;
    this.master.gain.setTargetAtTime(v, this.ac.currentTime, 0.05);
  }

  dispose() {
    this._stopTone();
    try { this.master.disconnect(); this.limiter.output.disconnect(); } catch { /* ignore */ }
  }
}
